// @ts-nocheck

import { TUnknownParam } from '@utils/diff/helpers';

type TCallback = (...args: TUnknownParam[]) => void | Promise<void>;
export {};

class Emitter {
  constructor() {
    Object.defineProperty(this, 'listeners', { value: {}, writable: true, configurable: true });
  }
  addEventListener(type: string, callback: TCallback, options: TUnknownParam): void {
    if (!(type in this.listeners)) {
      this.listeners[type] = [];
    }
    this.listeners[type].push({ callback, options });
  }
  removeEventListener(type: string, callback: TCallback): void {
    if (!(type in this.listeners)) {
      return;
    }
    const stack: TCallback[] = this.listeners[type];
    for (let i: number = 0, l: number = stack.length; i < l; i++) {
      if (stack[i].callback === callback) {
        stack.splice(i, 1);
        return;
      }
    }
  }
  dispatchEvent(event: string): boolean | undefined {
    if (!(event.type in this.listeners)) {
      return;
    }
    const stack: TCallback[] = this.listeners[event.type];
    const stackToCall: TCallback[] = stack.slice();
    for (let i: number = 0, l: number = stackToCall.length; i < l; i++) {
      const listener: TCallback = stackToCall[i];
      try {
        listener.callback.call(this, event);
      } catch (e) {
        Promise.resolve().then(() => {
          throw e;
        });
      }
      if (listener.options && listener.options.once) {
        this.removeEventListener(event.type, listener.callback);
      }
    }
    return !event.defaultPrevented;
  }
}

export class AbortSignal extends Emitter {
  constructor() {
    super();
    if (!this.listeners) {
      Emitter.call(this);
    }

    Object.defineProperty(this, 'aborted', { value: false, writable: true, configurable: true });
    Object.defineProperty(this, 'onabort', { value: null, writable: true, configurable: true });
    Object.defineProperty(this, 'reason', { value: undefined, writable: true, configurable: true });
  }
  toString(): string {
    return '[object AbortSignal]';
  }
  dispatchEvent(event: string): void {
    if (event.type === 'abort') {
      this.aborted = true;
      if (typeof this.onabort === 'function') {
        this.onabort.call(this, event);
      }
    }

    super.dispatchEvent(event);
  }
}

export class AbortController {
  constructor() {
    Object.defineProperty(this, 'signal', { value: new AbortSignal(), writable: true, configurable: true });
  }
  abort(reason: string | undefined): void {
    const event: Event = new Event('abort');

    let signalReason: string | undefined = reason;
    if (signalReason === undefined) {
      signalReason = new Error('This operation was aborted');
      signalReason.name = 'AbortError';
    }
    this.signal.reason = signalReason;

    this.signal.dispatchEvent(event);
  }
  toString(): string {
    return '[object AbortController]';
  }
}

function polyfillNeeded(self: typeof self): boolean {
  return (
    (typeof self.Request === 'function' && !Object.prototype.hasOwnProperty.call(self.Request.prototype, 'signal')) || !self.AbortController
  );
}

(function (self: typeof self) {
  'use strict';

  if (!polyfillNeeded(self)) {
    return;
  }

  if (typeof Symbol !== 'undefined' && Symbol.toStringTag) {
    AbortController.prototype[Symbol.toStringTag] = 'AbortController';
    AbortSignal.prototype[Symbol.toStringTag] = 'AbortSignal';
  }

  self.AbortController = AbortController;
  self.AbortSignal = AbortSignal;
})(typeof self !== 'undefined' ? self : global);
